import React, { useState } from 'react';
import { useOpeningHours, formatSpecialDay } from '../redesign/useBusinessInfo';

// Live hours from GET /api/opening-hours (shared cached fetch in useBusinessInfo).
const OpeningHours = ({ className = "", compact = false }) => {
  const data = useOpeningHours();
  const [showDetails, setShowDetails] = useState(false);
  const openingHours = { ...data, hours: data.summary.join(" · ") };
  const specialDays = data.special_days || [];

  const hasNotices = openingHours.closed_on_tuesdays || openingHours.holiday_hours_notice || specialDays.length > 0;

  // Compact mode for header - just hours with indicator
  if (compact) {
    return (
      <div className="relative inline-block" data-testid="opening-hours">
        <div className="flex items-center gap-2">
          <span className={className}>{openingHours.hours}</span>
          {hasNotices && (
            <button
              className="px-2 py-1 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-full animate-pulse hover:animate-none transition-all cursor-pointer whitespace-nowrap"
              onMouseEnter={() => setShowDetails(true)}
              onMouseLeave={() => setShowDetails(false)}
              onClick={() => setShowDetails(!showDetails)}
              data-testid="hours-notice-btn"
            >
              Opening Hours Notice
            </button>
          )}
        </div>

        {/* Popover with details */}
        {showDetails && hasNotices && (
          <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 text-left">
            <div className="space-y-3">
              <div className="font-semibold text-gray-900 border-b pb-2">
                Opening Hours
              </div>
              <div className="text-sm text-gray-700">
                {openingHours.hours}
              </div>
              
              {openingHours.closed_on_tuesdays && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-sm" data-testid="closed-tuesdays">
                  <div className="font-semibold text-red-900">⚠️ Closed on Tuesdays</div>
                </div>
              )}
              
              {openingHours.holiday_hours_notice && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm" data-testid="holiday-notice">
                  <div className="font-semibold text-amber-900 mb-1">⚠️ Seasonal Closure</div>
                  <div className="text-amber-800">
                    Umoona Opal Mine is seasonally closed for entire month of February. We will re-open to the public on 2 March 2026.
                  </div>
                </div>
              )}

              {specialDays.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm" data-testid="special-days">
                  <div className="font-semibold text-amber-900 mb-1">Upcoming changes</div>
                  {specialDays.map((d) => (
                    <div key={d.date} className="text-amber-800">{formatSpecialDay(d)}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full mode for footer and other places - show everything
  return (
    <div className={className} data-testid="opening-hours">
      <div className="font-medium">{openingHours.hours}</div>
      
      {openingHours.closed_on_tuesdays && (
        <div className="text-sm mt-2 text-red-600 font-medium" data-testid="closed-tuesdays">
          ⚠️ Closed on Tuesdays
        </div>
      )}
      
      {openingHours.holiday_hours_notice && (
        <div className="text-sm mt-2 text-amber-600" data-testid="holiday-notice">
          <div className="font-semibold">⚠️ Seasonal Closure</div>
          <div className="mt-1">Umoona Opal Mine is seasonally closed for entire month of February. We will re-open to the public on 2 March 2026.</div>
        </div>
      )}

      {specialDays.length > 0 && (
        <div className="text-sm mt-2 text-amber-600" data-testid="special-days">
          <div className="font-semibold">Upcoming changes</div>
          {specialDays.map((d) => (
            <div key={d.date} className="mt-1">{formatSpecialDay(d)}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OpeningHours;
